/**
 * Phone number formatting, validation, and normalization utilities.
 *
 * The backend stores phone numbers in E.164 format (+XXXXXXXXXXX).
 * These helpers convert between E.164 and display format, and validate
 * user input before submission. Supports international numbers.
 */

/** Known country code prefixes sorted longest-first for matching. */
const COUNTRY_CODE_PREFIXES = [
  '+972', '+353', '+1', '+44', '+49', '+52', '+55', '+61',
  '+64', '+65', '+81', '+82', '+86', '+91', '+33',
].sort((a, b) => b.length - a.length);

/**
 * Format a phone number for display.
 * - US/CA E.164 (+1XXXXXXXXXX) → (555) 123-4567
 * - Other E.164 → +CC XXXXXXXXXX (grouped with spaces)
 * - Raw 10 digits → (555) 123-4567
 * Returns the original string unchanged if it can't be parsed.
 */
export function formatPhoneDisplay(phone: string | null | undefined): string {
  if (!phone) return '';

  // US/CA formatting for +1
  if (phone.startsWith('+1') && phone.length === 12) {
    const d = phone.slice(2);
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  }

  // Raw 10 digits (assumed US)
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }

  // International E.164 — show code + space + number
  if (phone.startsWith('+')) {
    for (const prefix of COUNTRY_CODE_PREFIXES) {
      if (phone.startsWith(prefix)) {
        const local = phone.slice(prefix.length);
        return `${prefix} ${local}`;
      }
    }
    // Unknown code — just add a space after the code section
    return phone;
  }

  return phone;
}

/**
 * Validate a phone number input.
 * Returns null if valid, or an error message string.
 * Accepts US 10-digit, E.164 international, or country-code-prefixed numbers.
 */
export function validatePhone(phone: string | null | undefined): string | null {
  if (!phone || !phone.trim()) return null; // empty is valid (field may be optional)

  // Already E.164 format
  if (phone.startsWith('+')) {
    const digits = phone.slice(1).replace(/\D/g, '');
    if (digits.length >= 7 && digits.length <= 15) return null;
    return 'Enter a valid phone number (7-15 digits with country code)';
  }

  // Raw digits — check for valid US number
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) return null;
  if (digits.length === 11 && digits[0] === '1') return null;

  return 'Enter a valid phone number';
}


